import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, MessageCircle, ShieldCheck } from "lucide-react";
import productsData from "@/data/products.json";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { useEnquiry } from "@/context/EnquiryContext";
import { useLang } from "@/context/LanguageContext";
import { BRAND } from "@/lib/site";

export default function ProductDetail() {
  const { slug } = useParams();
  const { openEnquiry } = useEnquiry();
  const { t, tp, tcat, tprice, thl } = useLang();
  const product = productsData.find((p) => p.slug === slug);

  if (!product) {
    return (
      <div className="pt-40 pb-32 text-center px-6" data-testid="product-not-found">
        <h1 className="font-serif text-5xl text-white">Product not found</h1>
        <Link to="/products" className="mt-6 inline-block text-gold underline underline-offset-4">
          {t("all_products")}
        </Link>
      </div>
    );
  }

  const related = productsData.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 3);
  const name = tp(product, "name");

  return (
    <div className="pt-32 pb-24" data-testid="product-detail-page">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Link
          to="/products"
          data-testid="product-back-link"
          className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-white/60 hover:text-gold transition-colors"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> {t("all_products")}
        </Link>

        <div className="mt-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative lg:sticky lg:top-28 h-max overflow-hidden rounded-3xl border border-gold/20 bg-navy-800"
          >
            {product.fit === "cover" ? (
              <img src={product.image} alt={name} className="relative w-full h-[420px] md:h-[560px] object-cover" />
            ) : (
              <>
                <div className="absolute inset-0 radial-glow" />
                <img src={product.image} alt={name} className="relative w-full object-contain p-10 md:p-14 drop-shadow-[0_25px_50px_rgba(0,0,0,0.55)]" />
              </>
            )}
            <span className="absolute top-5 left-5 rounded-full border border-gold/30 bg-navy-950/60 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-gold">
              {tcat(product.category)}
            </span>
          </motion.div>

          <div>
            <Reveal>
              <p className="label-eyebrow">{tp(product, "tagline")}</p>
              <h1 className="mt-3 font-serif font-light text-4xl md:text-6xl text-white leading-[1.02]">{name}</h1>
              <div className="mt-4 flex items-center gap-3 text-sm">
                <span className="text-gold">{product.weight}</span>
                <span className="h-1 w-1 rounded-full bg-white/30" />
                <span className="text-white/55">{tprice(product.priceLabel)}</span>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="mt-7 text-white/65 font-light leading-relaxed text-lg">
              {tp(product, "description")}
            </Reveal>

            <Reveal delay={0.15} className="mt-8">
              <h3 className="text-xs uppercase tracking-[0.2em] text-white/45 mb-4">{t("detail_highlights")}</h3>
              <div className="grid grid-cols-2 gap-3">
                {product.highlights.map((h) => (
                  <div key={h} className="flex items-center gap-2.5 rounded-lg border border-gold/15 bg-navy-800 px-4 py-3">
                    <Check size={15} className="text-gold shrink-0" />
                    <span className="text-sm text-white/80">{thl(h)}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2} className="mt-8">
              <h3 className="text-xs uppercase tracking-[0.2em] text-white/45 mb-4">{t("detail_specs")}</h3>
              <dl className="divide-y divide-white/10 border-y border-white/10">
                {Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-3">
                    <dt className="text-sm text-white/50">{k}</dt>
                    <dd className="text-sm text-white/90 text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <Reveal delay={0.25} className="mt-9">
              <button
                onClick={() => openEnquiry(name)}
                data-testid="product-enquire-btn"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-gold px-9 py-4 text-sm uppercase tracking-[0.2em] font-semibold text-navy-900 hover:bg-gold-light transition-colors"
              >
                <MessageCircle size={18} /> {t("enquire_whatsapp")}
              </button>
              <p className="mt-4 flex items-center gap-2 text-xs text-white/45">
                <ShieldCheck size={14} className="text-gold" /> FSSAI {BRAND.fssai} • {t("brand_by")} {BRAND.company}
              </p>
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-28">
            <h2 className="font-serif font-light text-3xl md:text-4xl text-white mb-8">
              {t("detail_also")} <span className="text-gold-gradient italic">{t("detail_like")}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
